/** src/components/cart/Cart.jsx
 * Cart component for the e-commerce application
 * Displays cart items, allows quantity adjustments, and provides checkout functionality
 *
 * @module Cart
 * @requires React
 * @requires @emotion/styled
 * @requires lucide-react
 * @requires @sentry/react
 * @requires context/CartContext
 * @requires utils/cartUtils
 * @requires utils/price
 * @requires ./ItemComponent
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { X } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { useCart } from 'context/CartContext';
import { useCheckout } from 'context/CheckoutContext';
import { useContent } from 'context/ContentContext';
import { formatDisplayPrice } from 'utils/price';
import ItemComponent from './ItemComponent';

// Semi-transparent overlay that covers the page when cart is open
const CartOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${({ theme }) => theme.zIndices.modal};
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

// Main container for the cart drawer that slides in from the right
const CartContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 100%;
  max-width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: ${({ theme }) => theme.zIndices.modal + 1};
  overflow-y: auto;
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.shadows.lg};

  ${({ theme }) => theme.mediaQueries.md} {
    width: 500px;
    max-width: 90%;
  }
`;

// Header section containing the title and close button
const CartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
`;

const CartTitle = styled.h2`
  ${({ theme }) => theme.mixins.textH3};
  margin: 0;
`;

// Button to close the cart drawer
const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`;

// Main content area containing cart items
const CartContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

// Message displayed when the cart is empty
const EmptyCartMessage = styled.p`
  ${({ theme }) => theme.mixins.textP1};
  text-align: center;
  margin: 40px 0;
  color: ${({ theme }) => theme.colors.darkGray};
`;

// List container for cart items
const CartItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

// Footer section containing total and action buttons
const CartFooter = styled.div`
  padding: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  background-color: ${({ theme }) => theme.colors.white};
`;

// Container for displaying the total amount
const CartTotal = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const TotalLabel = styled.span`
  ${({ theme }) => theme.mixins.textH4};
`;

const TotalAmount = styled.span`
  ${({ theme }) => theme.mixins.textH4};
  color: ${({ theme }) => theme.colors.primary};
`;

// Container for the action buttons (empty cart and checkout)
const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: 1fr 2fr;
  }
`;

// Primary button for checkout action
const CheckoutButton = styled.button`
  width: 100%;
  padding: 15px;
  background-color: ${({ theme }) => theme.colors.tertiary};
  color: white;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  ${({ theme }) => theme.mixins.textP1};
  font-weight: 700;
  letter-spacing: 0.03em;
  transition: all 0.25s ease;
  box-shadow: ${({ theme }) =>
    `0 6px 20px ${theme.colors.tertiary}30, inset 0 1px 0 rgba(255,255,255,0.12)`};
  grid-column: 1 / -1;

  ${({ theme }) => theme.mediaQueries.sm} {
    grid-column: 2 / 3;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => `0 8px 24px ${theme.colors.tertiary}40`};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.tertiary};
    outline-offset: 2px;
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.lightGray};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

// Secondary button for emptying the cart
const EmptyCartButton = styled.button`
  width: 100%;
  padding: 15px;
  background: ${({ theme }) => `${theme.colors.primary}14`};
  color: ${({ theme }) => theme.colors.primary};
  border: 1.5px solid ${({ theme }) => theme.colors.primary};
  border-radius: 14px;
  cursor: pointer;
  ${({ theme }) => theme.mixins.textP1};
  font-weight: 700;
  letter-spacing: 0.03em;
  transition: all 0.25s ease;
  grid-column: 1 / -1;

  ${({ theme }) => theme.mediaQueries.sm} {
    grid-column: 1 / 2;
  }

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: transparent;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => `0 8px 24px ${theme.colors.primary}40`};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  &:disabled {
    border-color: ${({ theme }) => theme.colors.lightGray};
    color: ${({ theme }) => theme.colors.lightGray};
    background: transparent;
    cursor: not-allowed;
  }
`;

/**
 * Cart component - Provides a sliding drawer interface for managing shopping cart
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the cart drawer is open
 * @param {Function} props.onClose - Function to call when the cart should be closed
 * @returns {React.ReactElement} The rendered Cart component
 */
const Cart = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { messages } = useContent();
  const {
    updateCartItemQuantity,
    removeCartItem,
    clearCart,
    getProcessedCartItems,
  } = useCart();

  /**
   * Get processed cart items for display
   * These are already formatted with all necessary information
   *
   * @returns {Array} Processed cart items with complete information
   */
  const cartItemsArray = useMemo(() => {
    // Our new getProcessedCartItems function already returns the proper format
    return getProcessedCartItems();
  }, [getProcessedCartItems]);

  /**
   * Calculates the total price of all items in the cart
   * Re-calculates when cart items or their quantities change
   *
   * @returns {number} The total price
   */
  const totalPrice = useMemo(() => {
    const total = cartItemsArray.reduce((sum, item) => {
      return sum + item.subtotal;
    }, 0);
    return total;
  }, [cartItemsArray]);

  /**
   * Handles incrementing the quantity of an item
   *
   * @param {Object} item - The cart item to increment
   */
  const handleIncrement = (item) => {
    try {
      // Use updateCartItemQuantity from useCart
      updateCartItemQuantity(item.id, item.quantity + 1);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Cart', action: 'incrementItem' },
      });
    }
  };

  /**
   * Handles decrementing the quantity of an item
   *
   * @param {Object} item - The cart item to decrement
   */
  const handleDecrement = (item) => {
    try {
      // Use updateCartItemQuantity from useCart (it handles removal if quantity becomes 0)
      updateCartItemQuantity(item.id, item.quantity - 1);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Cart', action: 'decrementItem' },
      });
    }
  };

  /**
   * Handles completely removing an item from the cart
   *
   * @param {Object} item - The cart item to remove
   */
  const handleRemove = (item) => {
    try {
      // Use removeCartItem from useCart
      removeCartItem(item.id);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Cart', action: 'removeItem' },
      });
    }
  };

  /**
   * Empties the entire cart by removing all items
   */
  const handleEmptyCart = () => {
    try {
      // Use clearCart from useCart
      clearCart();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Cart', action: 'emptyCart' },
      });
    }
  };

  /**
   * Initiates the checkout process
   * If an order exists and cart has changed, recreates the order first
   */
  const { recreateOrder, orderResponse, hasCartChanged } = useCheckout();
  const [isRecreating, setIsRecreating] = useState(false);

  const handleProceedToCheckout = async () => {
    try {
      // Check if we need to recreate the order
      // This happens when user has reached payment screen, goes back to menu, and adds more items
      if (orderResponse && hasCartChanged) {
        setIsRecreating(true);

        // Log recreation attempt
        Sentry.addBreadcrumb({
          message: 'Recreating order due to cart changes',
          category: 'checkout',
          level: 'info',
          data: {
            orderId: orderResponse?.order?.id,
            cartItemsCount: cartItemsArray.length,
          },
        });

        // Attempt to recreate order (delete old, prepare for new)
        const result = await recreateOrder();

        if (!result.success) {
          // Log error but don't block checkout flow
          Sentry.captureMessage(
            'Order recreation failed, continuing to checkout',
            {
              level: 'warning',
              tags: {
                component: 'Cart',
                action: 'handleProceedToCheckout',
              },
              extra: {
                error: result.error,
                orderId: orderResponse?.order?.id,
              },
            },
          );
        }

        setIsRecreating(false);
      }

      // Navigate to checkout (Step 1 - Contact Info)
      // User will review their info and create a new order
      navigate('/checkout');
    } catch (error) {
      // Catch any unexpected errors
      Sentry.captureException(error, {
        tags: {
          component: 'Cart',
          action: 'handleProceedToCheckout',
        },
        extra: {
          orderId: orderResponse?.order?.id,
          hasCartChanged,
        },
      });

      setIsRecreating(false);

      // Still navigate to checkout - user can try again
      navigate('/checkout');
    }
  };

  /**
   * Handles keyboard interaction for accessibility
   * Closes the cart when Escape key is pressed
   *
   * @param {KeyboardEvent} event - The keyboard event
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  // The drawer rarely has focus right after a tap on the cart icon, so the
  // onKeyDown above never fired on phones. Listen on the document while open
  // (#245).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onDocKeyDown);
    return () => document.removeEventListener('keydown', onDocKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      <CartOverlay isOpen={isOpen} onClick={onClose} />
      <CartContainer
        isOpen={isOpen}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping Cart"
        tabIndex={isOpen ? 0 : -1}
      >
        <CartHeader>
          <CartTitle>{messages?.cart?.title || 'Your Cart'}</CartTitle>
          <CloseButton
            onClick={onClose}
            aria-label={messages?.cart?.closeCart || 'Close cart'}
          >
            <X size={24} />
          </CloseButton>
        </CartHeader>

        <CartContent>
          {cartItemsArray.length === 0 ? (
            <EmptyCartMessage>
              {messages?.emptyCart || 'Your cart is empty'}
            </EmptyCartMessage>
          ) : (
            <CartItemList aria-label="Cart items">
              {cartItemsArray.map((item) => (
                <ItemComponent
                  key={item.id}
                  item={item}
                  onIncrement={handleIncrement}
                  onDecrement={handleDecrement}
                  onRemove={handleRemove}
                />
              ))}
            </CartItemList>
          )}
        </CartContent>

        <CartFooter>
          <CartTotal>
            <TotalLabel>{messages?.cart?.total || 'Total'}:</TotalLabel>
            <TotalAmount>{formatDisplayPrice(totalPrice)}</TotalAmount>
          </CartTotal>

          <ButtonsContainer>
            <EmptyCartButton
              disabled={cartItemsArray.length === 0}
              onClick={handleEmptyCart}
              aria-label={messages?.cart?.emptyCart || 'Empty cart'}
            >
              {messages?.cart?.emptyCart || 'Empty Cart'}
            </EmptyCartButton>

            <CheckoutButton
              disabled={cartItemsArray.length === 0 || isRecreating}
              onClick={handleProceedToCheckout}
              aria-label={
                isRecreating
                  ? messages?.cart?.updatingOrder ||
                    'Updating order, please wait'
                  : messages?.cart?.proceedToCheckout || 'Proceed to checkout'
              }
            >
              {isRecreating
                ? messages?.cart?.updatingOrder || 'Updating Order...'
                : messages?.cart?.proceedToCheckout || 'Proceed to Checkout'}
            </CheckoutButton>
          </ButtonsContainer>
        </CartFooter>
      </CartContainer>
    </>
  );
};

export default Cart;
